import { Application, Color, Container, Graphics } from 'pixi.js';
import {
    BaseWidget,
    WidgetPosition,
} from 'ff/debrief/components/hud/widgets/BaseWidget';
import { CurrentPoint } from 'ff/debrief/models';
import { HudContext } from 'ff/debrief/components/hud/HudContext';
import { HudPositionTranslator } from 'ff/debrief/components/hud/utils';
import { HeadingTapeWidget } from 'ff/debrief/components/hud/widgets/HeadingTapeWidget';

/**
 * Composite widget that displays heading information.
 * Contains a horizontal heading tape with centered digital heading display.
 * Designed to be placed across the top of the HUD, with space reserved on the left for other widgets.
 */
export class HeadingSection extends BaseWidget {
    static readonly requiredProperties = ['magneticHeading'] as const;
    static readonly optionalProperties = [] as const;

    private headingTape: HeadingTapeWidget;

    override async init(
        pixiApp: Application,
        hudContext: HudContext,
        position: WidgetPosition
    ): Promise<void> {
        await super.init(pixiApp, hudContext, position);

        // Initialize heading tape to fill the entire section
        this.headingTape = new HeadingTapeWidget();
        await this.headingTape.init(
            pixiApp,
            hudContext,
            this.positionTranslator.translate({
                x: 0,
                y: 0,
                width: 1,
                height: 1,
            }),
            {},
            this.elementContainer
        );
    }

    override async populateElementContainer(
        elementContainer: Container,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): Promise<void> {
        await this.headingTape.populate();

        // Place a semi-transparent background behind the entire section
        const background = new Graphics()
            .rect(
                positionTranslator.x(0),
                positionTranslator.y(0),
                positionTranslator.x(1),
                positionTranslator.y(1)
            )
            .fill(new Color([0.8, 0.8, 0.8, 0.2]));
        elementContainer.addChildAt(background, 0);
    }

    protected updateWidget(point: CurrentPoint, hudContext: HudContext): void {
        this.headingTape.update(point, hudContext);
    }

    cleanUp(): void {
        this.headingTape?.destroy();
        this.headingTape = null;
    }
}
